import { useState } from "react";
import { ArrowLeft, Bell, /* BellOff, */ Volume2, VolumeX } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [dndEnabled, setDndEnabled] = useState(true);
  const [weekendNotificationsEnabled, setWeekendNotificationsEnabled] = useState(false);

  const [notifications, setNotifications] = useState([
    {
      title: "Meal Reminders",
      items: [
        { id: 1, label: "Breakfast reminder", enabled: true },
        { id: 2, label: "Lunch reminder", enabled: true },
        { id: 3, label: "Dinner reminder", enabled: false },
        { id: 4, label: "Water intake reminder", enabled: true },
      ],
    },
    {
      title: "Activity Notifications",
      items: [
        { id: 5, label: "Daily step goal achieved", enabled: true },
        { id: 6, label: "Inactivity alerts", enabled: false },
        { id: 7, label: "Workout suggestions", enabled: true },
      ],
    },
    {
      title: "Health Alerts",
      items: [
        { id: 8, label: "Medication reminders", enabled: true },
        { id: 9, label: "Sleep schedule reminder", enabled: true },
        { id: 10, label: "Heart rate alerts", enabled: false },
      ],
    },
    {
      title: "App Updates",
      items: [
        { id: 11, label: "Feature updates", enabled: true },
        { id: 12, label: "Tips and insights", enabled: true },
        { id: 13, label: "Weekly summary", enabled: true },
      ],
    },
  ]);

  const toggleNotification = (groupIndex: number, itemId: number) => {
    setNotifications(prev =>
      prev.map((group, gIndex) =>
        gIndex === groupIndex
          ? {
              ...group,
              items: group.items.map(item =>
                item.id === itemId ? { ...item, enabled: !item.enabled } : item
              ),
            }
          : group
      )
    );
  };

  const toggleAllNotifications = () => {
    const allEnabled = notifications.every(group =>
      group.items.every(item => item.enabled)
    );

    setNotifications(prev =>
      prev.map(group => ({
        ...group,
        items: group.items.map(item => ({ ...item, enabled: !allEnabled })),
      }))
    );
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera/profilepage")}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-title2 font-bold text-foreground">Notifications</h1>
            <p className="text-caption1 text-muted-foreground">Manage your alerts</p>
          </div>
        </div>

        {/* Master Control */}
        <Card className="rounded-2xl border border-border bg-card p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#737DFE]/10 dark:bg-[#737DFE]/20">
                <Bell className="h-6 w-6 text-[#737DFE] dark:text-[#737DFE]" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">
                  All Notifications
                </h3>
                <p className="text-subhead text-muted-foreground">
                  Master control for all alerts
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAllNotifications}
              className="rounded-lg border-border"
            >
              Toggle All
            </Button>
          </div>
        </Card>

        {/* Notification Groups */}
        {notifications.map((group, groupIndex) => (
          <Card
            key={groupIndex}
            className="rounded-2xl border border-border bg-card p-5 shadow-lg"
          >
            <h3 className="mb-4 font-semibold text-foreground">
              {group.title}
            </h3>
            <div className="space-y-4">
              {group.items.map((item, itemIndex) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${
                        item.enabled
                          ? "bg-green-100 dark:bg-green-950"
                          : "bg-muted"
                      }`}
                    >
                      {item.enabled ? (
                        <Volume2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <VolumeX className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <span className="text-subhead text-foreground">
                      {item.label}
                    </span>
                  </div>
                  <div
                    className={`relative h-6 w-12 cursor-pointer rounded-full transition-colors ${
                      item.enabled ? "bg-[#737DFE]" : "bg-muted"
                    }`}
                    onClick={() => toggleNotification(groupIndex, item.id)}
                  >
                    <div
                      className={`absolute top-1 h-4 w-4 rounded-full bg-background transition-transform ${
                        item.enabled ? "right-1" : "left-1"
                      }`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}

        {/* Notification Schedule */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 font-semibold text-foreground">
            Notification Schedule
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">Do Not Disturb</p>
                <p className="text-subhead text-muted-foreground">
                  10:00 PM - 7:00 AM
                </p>
              </div>
              <div
                className={`relative h-6 w-12 cursor-pointer rounded-full transition-colors ${
                  dndEnabled ? "bg-[#737DFE]" : "bg-muted"
                }`}
                onClick={() => setDndEnabled(!dndEnabled)}
              >
                <div
                  className={`absolute top-1 h-4 w-4 rounded-full bg-background transition-transform ${
                    dndEnabled ? "right-1" : "left-1"
                  }`}
                />
              </div>
            </div>

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">
                  Weekend Notifications
                </p>
                <p className="text-subhead text-muted-foreground">
                  Reduce frequency on weekends
                </p>
              </div>
              <div
                className={`relative h-6 w-12 cursor-pointer rounded-full transition-colors ${
                  weekendNotificationsEnabled ? "bg-[#737DFE]" : "bg-muted"
                }`}
                onClick={() => setWeekendNotificationsEnabled(!weekendNotificationsEnabled)}
              >
                <div
                  className={`absolute top-1 h-4 w-4 rounded-full bg-background transition-transform ${
                    weekendNotificationsEnabled ? "right-1" : "left-1"
                  }`}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Notification History */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 font-semibold text-foreground">
            Recent Notifications
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="mt-2 h-2 w-2 rounded-full bg-[#737DFE]" />
              <div className="flex-1">
                <p className="text-subhead text-foreground">Water intake reminder</p>
                <p className="text-caption1 text-muted-foreground">2 hours ago</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-2 h-2 w-2 rounded-full bg-green-500" />
              <div className="flex-1">
                <p className="text-subhead text-foreground">
                  Daily step goal achieved!
                </p>
                <p className="text-caption1 text-muted-foreground">5 hours ago</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-2 h-2 w-2 rounded-full bg-orange-500" />
              <div className="flex-1">
                <p className="text-subhead text-foreground">Lunch reminder</p>
                <p className="text-caption1 text-muted-foreground">Yesterday</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default NotificationsPage;
